/**
 * View Switcher Component
 *
 * Allows clients to switch between their personal view and client view
 * (mentor-managed data). Only visible to users who have active mentor relationships.
 */

import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getUserProfile } from "../../services/userService";
import { useScope } from "../../hooks/useScope";
import "./ViewSwitcher.css";

export const ViewSwitcher = () => {
  const { currentUser } = useAuth();
  const history = useHistory();
  const {
    isManaged,
    isClientView,
    mentorId,
    enterClientViewScope,
    exitManagedScope,
  } = useScope();
  const [activeMentors, setActiveMentors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      loadActiveMentors();
    }
  }, [currentUser]);

  const loadActiveMentors = async () => {
    try {
      setLoading(true);
      const userProfile = await getUserProfile(currentUser.uid);

      if (userProfile && userProfile.managedBy) {
        const mentors = [];

        for (const [mid, mentorData] of Object.entries(userProfile.managedBy)) {
          if (mentorData.status === "active") {
            mentors.push({
              mentorId: mid,
              ...mentorData,
            });
          }
        }

        setActiveMentors(mentors);
      }
    } catch (error) {
      console.error("Error loading active mentors:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchToPersonal = () => {
    console.log("[ViewSwitcher] Switching to personal view");
    exitManagedScope();
    // Redirect to /history to ensure we land on an allowed page
    history.push("/history");
  };

  const handleSwitchToMentorView = (mid) => {
    console.log("[ViewSwitcher] Switching to mentor view, mentorId:", mid);
    // Use enterClientViewScope for client viewing mentor-created data
    // effectiveUserId will be currentUser.uid (client viewing their own data)
    // mentorId will be used to filter for mentor-created content
    enterClientViewScope(mid, "Mentor's Content");
    // Redirect to /history to ensure we land on an allowed page
    history.push("/history");
  };

  // Don't show if user has no active mentors
  if (loading || activeMentors.length === 0) {
    return null;
  }

  // Don't show ViewSwitcher when mentor is masquerading as client
  // Mentors use the MasqueradeBanner to exit, not ViewSwitcher
  const isMentorMasquerade = isManaged && !isClientView;
  if (isMentorMasquerade) {
    return null;
  }

  // Check if currently in client view mode (viewing mentor-created content)
  const isInClientView = isManaged && isClientView;
  
  // Check if in personal view (not in any managed scope)
  const isInPersonalView = !isManaged;

  return (
    <div className="view-switcher">
      <div className="view-switcher-label">View:</div>
      <div className="view-switcher-buttons">
        <button
          className={`view-btn-view-switcher ${isInPersonalView ? "active" : ""}`}
          onClick={handleSwitchToPersonal}
          disabled={isInPersonalView}
        >
          Personal
        </button>
        <button
          className={`view-btn-view-switcher ${isInClientView ? "active" : ""}`}
          onClick={() => handleSwitchToMentorView(activeMentors[0].mentorId)}
          disabled={isInClientView}
        >
          Mentor's View
        </button>
      </div>
    </div>
  );
};

export default ViewSwitcher;
